import React from 'react';
import { Drawer, Table, Tag, Spin, Empty, Alert, Space, Typography, Tooltip } from 'antd';
import DateWithWeekday from '../../components/DateWithWeekday';
import { contributionUnit } from './range';
import { toTableRows } from './groupRows';
import useMaskCloseGuard from '../../components/useMaskCloseGuard';
import { FONT } from "../../styles/typography";
import { FAINT_COLOR, MARK_MUTED_COLOR, MUTED_COLOR } from '../../components/metricColors';
import { withKeyboardSort } from '../../components/keyboardSort';

const POS = '#52c41a';
const NEG = '#cf1322';

const money = (v) => (v ? `NT$${Math.round(v).toLocaleString()}` : '—');

/**
 * 訂單明細清單。三個入口共用同一個元件與同一支端點 (pms/ops/pickup-orders)：
 *
 *   訂房增速 · 依入住日   basis=pickup  某住用桶在近 N 個訂房日進出了什麼
 *   訂房增速 · 依訂房日   basis=pickup  某訂房日對前瞻窗口進出了什麼
 *   訂房曲線抽屜          basis=otb     某住用區間現在手上有什麼
 *
 * 兩種 basis 的欄位完全相同 —— 差別只在 pickup 有新增/取消兩半 (貢獻帶正負)，
 * otb 全是還活著的單 (只有正值)。所以不是各寫一張表，而是同一組欄位依 basis 換標題與樣式；
 * 分歧只存在於三個地方 (動作日欄、貢獻欄、合計列)，其餘一律共用。
 *
 * 「貢獻/在手」欄的單位隨住用區間長度而變 (單日桶的重疊天數恆為 1，該值就等於間數)。
 *
 * 沒有訂房人姓名以外的個資 —— 這張表是拿來判斷「這是被什麼撐起來的」，
 * 房型、專案、訂房單位就夠了，多帶只會讓這個畫面多一層不必要的暴露面。
 */
const zh = (a, b) => String(a || '').localeCompare(String(b || ''), 'zh-Hant');
const num = (k) => (a, b) => (a[k] || 0) - (b[k] || 0);

/**
 * 主列上不一致的欄位怎麼顯示。
 *
 * groupRows 把「各明細不同」表示成 null，這裡把 null 換成範圍字串並轉灰 ——
 * 灰色是在講「這不是一個值，是一個範圍」，避免被當成該訂單確定的房價或入住日。
 */
const varied = (text) => <span style={{ color: MUTED_COLOR }}>{text}</span>;
const orNull = (r, key, render) => (r[key] != null ? render(r[key]) : varied(r._range[key]));

/**
 * 欄寬一律寫死，且每一格都 nowrap (見 App.less 的 .order-lookup-table)。
 *
 * 這張表的欄位長度落差很大 —— 房型「禾風雙人房浴缸房」、專案「司導售價(需KEY價格)」、
 * 入住日還會多掛一個假日標籤 (中秋節/例假日)。交給自動分配的話長內容會被擠成兩三行，
 * 列高參差不齊，而這張表的用法是快速掃「哪一筆把柱撐起來的」，掃讀對不齊就沒用了。
 * 寬度含排序鈕佔的位置：每個標題右側都多了約 22px。
 * 放不下就水平捲動 (scroll.x)，不壓縮欄寬。
 */
const COL_WIDTH = {
  // ikey 比編號本身寬約 30px：antd 把展開鈕塞在第一欄，不留位置編號會被擠掉
  // act 不掛星期與假日標籤，只放「新增/取消」標籤 + 日期；actOtb 少了標籤故更窄
  ikey: 165, seq: 75, act: 145, actOtb: 110, ci: 230, co: 230, sn: 80, room: 165, qnt: 80, ovlp: 105,
  // rent 要放得下不一致時的金額範圍 (3,400–12,060)，不是只放單一金額
  // party 要放得下最長的單位名 (「國內-雄獅旅行社股份有限公司」14 字)
  nights: 130, rent: 150, rate: 230, party: 240, src: 120, grp: 80,
};
/** 表寬 = 該模式實際用到的欄寬相加。act 與 actOtb 是同一欄的兩種寬度，只能算一次。 */
const tableWidth = (otb) => Object.entries(COL_WIDTH)
  .filter(([k]) => k !== (otb ? 'act' : 'actOtb'))
  .reduce((s, [, w]) => s + w, 0);

function columnsFor(stayFrom, stayTo, otb) {
  const unit = contributionUnit(stayFrom, stayTo);
  return [
    // 編號擺第一欄：這張表的用途是「看完拿去 PMS 處理」，沒有編號就只能看不能動。
    // 只放 IKEY 純文字方便直接複製；通路訂位編號 (RVRESERVE_NOS) 掛在 Tooltip —— 它有兩成是空的，
    // 且只有要回頭找 OTA 對帳時才用得到，獨立一欄會佔掉 220px 卻多數時候是空白
    { title: '訂單編號', dataIndex: 'ikey', key: 'ikey', width: COL_WIDTH.ikey,
      sorter: (a, b) => zh(a.ikey, b.ikey),
      // 子列不重複編號：它已經印在展開的主列上，每個子列再印一次只會讓人分不清哪些是同一張單
      render: (v, r) => {
        if (r._child) return null;
        return r.rsv_nos
          ? <Tooltip title={`通路訂位編號 ${r.rsv_nos}`}><span style={{ borderBottom: `1px dotted ${MARK_MUTED_COLOR}` }}>{v}</span></Tooltip>
          : v;
      } },
    // 一張訂單訂兩種房型 (或同房型分批) 會有多筆明細。主列顯示筆數，子列顯示自己的序號
    { title: '明細', dataIndex: 'seq', key: 'seq', align: 'right', width: COL_WIDTH.seq,
      sorter: (a, b) => zh(a.ikey, b.ikey) || (Number(a.seq) - Number(b.seq)),
      render: (v, r) => (r._parent ? varied(`${r._count} 筆`) : v) },
    // pickup：新增與取消同表、用號誌與顏色區分 —— 兩件事本來就是同一根柱的正負兩半，
    // 拆成兩張表反而看不出「今天進的量有沒有被同一天的退訂吃掉」。
    // 標題寫「動作日」而不是「動作」：這一欄排序是照日期排的，只寫「動作」會被讀成照新增/取消分組。
    // otb：全是還活著的單，沒有取消那一半，標籤只會是清一色的綠色噪音 ——
    // 改回單純的「成立日」，欄寬也隨之縮回
    { title: otb ? '成立日' : '動作日', key: 'act', width: otb ? COL_WIDTH.actOtb : COL_WIDTH.act,
      sorter: (a, b) => String(a.act_dat || '').localeCompare(String(b.act_dat || '')) || zh(a.kind, b.kind),
      render: (_, r) => (
        <Space size={4}>
          {otb ? null : (
            <Tag color={r.kind === 'added' ? 'green' : 'red'} style={{ marginInlineEnd: 0 }}>
              {r.kind === 'added' ? '新增' : '取消'}
            </Tag>
          )}
          {/* 動作日是訂單成立/取消的日子，不是住用日 —— 它的星期與假日身分不影響任何判斷，
              標了只是把最該掃的入住日那一欄的視覺重量稀釋掉 */}
          {orNull(r, 'act_dat', (d) => d)}
        </Space>
      ) },
    { title: '入住', dataIndex: 'ci', key: 'ci', width: COL_WIDTH.ci,
      sorter: (a, b) => String(a.ci || '').localeCompare(String(b.ci || '')),
      render: (_, r) => orNull(r, 'ci', (d) => <DateWithWeekday date={d} />) },
    // 退房日：後端一直有回 co，但表上只有入住 + 晚數，要知道「哪天走」得自己心算。
    // 團體是否卡在某一晚、隔天就空掉，看的正是這一欄
    { title: '退房', dataIndex: 'co', key: 'co', width: COL_WIDTH.co,
      sorter: (a, b) => String(a.co || '').localeCompare(String(b.co || '')),
      render: (_, r) => orNull(r, 'co', (d) => <DateWithWeekday date={d} />) },
    { title: '晚數', dataIndex: 'stay_nights', key: 'sn', align: 'right', width: COL_WIDTH.sn,
      sorter: num('stay_nights'), render: (_, r) => orNull(r, 'stay_nights', (v) => v) },
    // 房型與專案不同於日期，沒有「範圍」可言，改講種類數 —— 要看是哪幾種就展開子列
    { title: '房型', dataIndex: 'room_nam', key: 'room', width: COL_WIDTH.room,
      sorter: (a, b) => zh(a.room_nam, b.room_nam),
      render: (v, r) => (v != null ? v : varied(`${r._kinds.room_nam} 種`)) },
    { title: '間數', dataIndex: 'qnt', key: 'qnt', align: 'right', width: COL_WIDTH.qnt,
      sorter: num('qnt') },
    // 間數 × 窗內晚數 = 貢獻。把乘數攤開，看的人才不必猜 2 間怎麼變成 6 間夜
    { title: '窗內晚數', dataIndex: 'ovlp', key: 'ovlp', align: 'right', width: COL_WIDTH.ovlp,
      sorter: num('ovlp'), render: (_, r) => orNull(r, 'ovlp', (v) => v) },
    // 依絕對值排序而非帶號值：想看的是「影響最大的是哪幾筆」，正負兩端都算大。
    // 預設降冪與後端回傳順序一致，讓標題上的排序狀態如實反映目前的順序。
    // 在手模式沒有負值，號誌與顏色都是多餘的 —— 標題也改講「在手」而非「貢獻」
    { title: otb ? `在手 (${unit})` : `貢獻 (${unit})`, dataIndex: 'nights', key: 'nights', align: 'right', width: COL_WIDTH.nights,
      sorter: (a, b) => Math.abs(a.nights) - Math.abs(b.nights), defaultSortOrder: 'descend',
      render: (v) => (otb ? <b>{v}</b> : <b style={{ color: v >= 0 ? POS : NEG }}>{v >= 0 ? `+${v}` : v}</b>) },
    // 刻意不叫「純房價」：ORDER_DT.RENT_AMT 整筆訂單固定不變，實測比 RVUSE_DT 的逐晚房租
    // 平均高約兩成 (含餐)，與儀錶板其他地方的 ADR 不同口徑，混用會得出錯的結論
    { title: '訂單單價', dataIndex: 'rent', key: 'rent', align: 'right', width: COL_WIDTH.rent,
      sorter: num('rent'), render: (_, r) => orNull(r, 'rent', money) },
    { title: '專案', dataIndex: 'rate_nam', key: 'rate', width: COL_WIDTH.rate,
      sorter: (a, b) => zh(a.rate_nam, b.rate_nam),
      render: (v, r) => (v != null ? v : varied(`${r._kinds.rate_nam} 種`)) },
    // 旅行社訂的就顯示旅行社。後端的 party 已經照「有訂房單位就用單位、否則用住客姓名」決定，
    // 另一個名字放 Tooltip —— 旅行社訂單想知道實際住客、散客訂單想知道有沒有掛單位時查得到
    { title: '訂房單位／住客', dataIndex: 'party', key: 'party', width: COL_WIDTH.party,
      sorter: (a, b) => zh(a.party, b.party),
      render: (v, r) => {
        if (!v) return <span style={{ color: FAINT_COLOR }}>—</span>;
        return r.party_alt
          ? <Tooltip title={`住客 ${r.party_alt}`}><span style={{ borderBottom: `1px dotted ${MARK_MUTED_COLOR}` }}>{v}</span></Tooltip>
          : v;
      } },
    { title: '來源', dataIndex: 'source', key: 'src', width: COL_WIDTH.src,
      sorter: (a, b) => zh(a.source, b.source) },
    { title: '團體', dataIndex: 'is_group', key: 'grp', align: 'center', width: COL_WIDTH.grp,
      sorter: (a, b) => (a.is_group ? 1 : 0) - (b.is_group ? 1 : 0),
      render: (v) => (v ? <Tag color="blue" style={{ marginInlineEnd: 0 }}>團</Tag> : null) },
  ];
}

/** 合計列。截斷時仍以後端在截斷前算好的合計顯示 —— 這一行必須永遠等於柱高。 */
function Totals({ data, stayFrom, stayTo, otb }) {
  const unit = contributionUnit(stayFrom, stayTo);
  const count = <Typography.Text type="secondary">共 {data.total_orders} 張訂單 · {data.total_rows} 筆明細</Typography.Text>;
  // 在手清單沒有取消那一半，講「新增/取消/淨」是把 0 當成資訊在報
  if (otb) {
    return (
      <Space size={16} style={{  }}>
        <span>在手 <b>{data.added}</b> {unit}</span>
        {count}
      </Space>
    );
  }
  return (
    <Space size={16} style={{  }}>
      <span>新增 <b style={{ color: POS }}>+{data.added}</b> {unit}</span>
      <span>取消 <b style={{ color: NEG }}>−{data.cancelled}</b> {unit}</span>
      <span>淨 <b style={{ color: data.net >= 0 ? POS : NEG }}>{data.net >= 0 ? '+' : ''}{data.net}</b> {unit}</span>
      {count}
    </Space>
  );
}

export default function OrdersDrawer({ open, title, loading, data, error, onClose, zIndex }) {
  const otb = data ? data.basis === 'otb' : false;
  // 開啟動畫期間遮罩是暴露的，行動裝置遲到的相容性點擊會打在上面把抽屜關掉
  const maskClosable = useMaskCloseGuard(open);
  const width = tableWidth(otb);
  const body = () => {
    if (error) return <Alert type="error" showIcon message={`訂單明細載入失敗：${error}`} />;
    if (!data) return null;
    if (!data.items.length) {
      return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE}
        description={otb ? '這段期間目前沒有在手訂單' : '這根柱底下沒有訂單'} />;
    }
    return (
      <>
        <div style={{ marginBottom: 8 }}>
          <Totals data={data} stayFrom={data.stay_from} stayTo={data.stay_to} otb={otb} />
          {/* 查了哪一段要寫出來。pickup 模式的柱桶邊界會被資料窗口夾掉 (例如最舊那根月柱只有月底幾天)，
              不寫的話看的人無從判斷筆數為何比預期少；otb 模式則要交代在手是「截至哪一天」還原的 */}
          <div style={{ fontSize: FONT.caption, color: FAINT_COLOR, marginTop: 2 }}>
            住用 {data.stay_from}–{data.stay_to}
            {otb ? ` · 在手還原至 ${data.asof}` : ` · 訂房 ${data.book_from}–${data.book_to}`}
          </div>
        </div>
        {/* 截斷要講出來。少列幾張本身不嚴重，但沉默地少列會被讀成「就只有這些」 */}
        {data.truncated ? (
          <Alert type="info" showIcon style={{ marginBottom: 8 }}
            message={`訂單數過多，僅列出${otb ? '在手最多' : '貢獻最大'}的 ${data.items.length} 張 (共 ${data.total_orders} 張)。上方合計仍為全部訂單的總和。`} />
        ) : null}
        {/* 不分頁：這張表是拿來掃「是被哪幾張單撐起來的」，答案分散在哪一頁沒人知道，
            翻頁只會讓人漏看。訂單數上限 500 已在後端擋住，一次全列不會失控。
            預設收合：多數訂單只有一筆明細、本來就沒有子列，展開全部只會把畫面拉長 */}
        <Table rowKey="_key" className="order-lookup-table"
          size="small" dataSource={toTableRows(data.items)} columns={withKeyboardSort(columnsFor(data.stay_from, data.stay_to, otb))}
          pagination={false} scroll={{ x: width }} />
      </>
    );
  };

  return (
    // 寬度剛好容得下整張表 (含抽屜左右內距)，窄螢幕則退回滿版 —— 讓水平捲動只在真的放不下時才出現。
    // zIndex 供疊在訂房曲線抽屜之上時指定：antd 的 Drawer 預設同層級，只靠 DOM 順序決勝
    <Drawer title={title} placement="right" width={`min(${width + 56}px, 100vw)`} zIndex={zIndex}
      visible={open} onClose={onClose} maskClosable={maskClosable}>
      <Spin spinning={loading}>{body()}</Spin>
    </Drawer>
  );
}
